import { formatDate } from '@/components/documents/format'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import {
  type ComplianceFile,
  type ComplianceSlot,
  SlotStatus,
  type UploadReceipt as Receipt,
} from '@/models/vault'

/**
 * "Got it" — shown the moment an upload lands, then sharpened once the parser
 * has read the file. The ask is the row of buttons: each opens the upload for a
 * slot this driver or vehicle is still missing.
 */
export function UploadReceipt({
  file,
  slot,
  receipt,
  onOpenUpload,
  onDismiss,
}: {
  file: ComplianceFile
  /** The slot the upload went into — known before the parser has said anything. */
  slot: ComplianceSlot
  receipt?: Receipt
  onOpenUpload: (slot: ComplianceSlot) => void
  onDismiss: () => void
}) {
  const slotsByKey = new Map(
    file.sections.flatMap((section) => section.slots.map((s) => [s.key, s] as const))
  )
  // Until the receipt arrives the file itself says what is still open.
  const missing = receipt
    ? receipt.missing.map((m) => slotsByKey.get(m.key)).filter((s): s is ComplianceSlot => !!s)
    : [...slotsByKey.values()].filter(
        (s) => s.required && s.status === SlotStatus.MISSING && s.key !== slot.key
      )

  const mismatch = receipt?.typeMatches === false
  const otherSubject = receipt?.otherSubject ?? null
  const expired = receipt?.slotStatus === SlotStatus.EXPIRED
  const variant = mismatch || otherSubject ? 'warning' : expired ? 'error' : 'info'

  const readAs = receipt?.readAsLabel ?? slot.label
  const expiry = receipt?.dateExpiration ? formatDate(receipt.dateExpiration) : null
  // "Looks like" is the parser's claim; without a read there is only the slot.
  const wasRead = !!receipt?.readAs

  return (
    <Banner variant={variant} onDismiss={onDismiss}>
      <div className="flex flex-col gap-2">
        <p>
          <span className="font-semibold">Got it</span>
          {receipt && wasRead ? (
            <>
              {' — this looks like '}
              {file.name}&rsquo;s {readAs.toLowerCase()}
              {expiry ? `, expires ${expiry}` : ''}.
            </>
          ) : receipt ? (
            <>
              {' — '}
              {slot.label.toLowerCase()} for {file.name}. We couldn&rsquo;t read the file, so check it
              yourself.
            </>
          ) : (
            <>
              {' — '}
              {slot.label.toLowerCase()} for {file.name}. Reading it now.
            </>
          )}
        </p>
        {mismatch && (
          <p>
            It reads as a {readAs.toLowerCase()}, not a {slot.label.toLowerCase()} — filed as uploaded, but
            worth a look.
          </p>
        )}
        {otherSubject && (
          <p>
            It names {otherSubject}, but it&rsquo;s filed under {file.name}.
          </p>
        )}
        {expired && <p>It&rsquo;s already expired.</p>}
        {missing.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>Still missing for {file.name}:</span>
            {missing.map((s) => (
              <Button key={s.key} size="xs" variant="outline" onClick={() => onOpenUpload(s)}>
                {s.label}
              </Button>
            ))}
          </div>
        ) : (
          <p>That completes {file.name}&rsquo;s file.</p>
        )}
      </div>
    </Banner>
  )
}
